import { moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { ContextMenuComponent } from '../../context-menu/context-menu.component';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, flatten, isEqual, last } from 'lodash';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { ResourceUUIDVersionIdentifier } from 'src/app/common/model/resource-id-version-identifier';
import { ToastService } from 'src/app/common/service/toast.service';
import { SetOfColumnsService } from 'src/app/shared/service/set-of-colums.service';
import { anyFieldsTrueValidatorFormArray } from 'src/app/shared/util/validators';
import { ColumnVisabilityPositionSetting } from '../../../model/data-source';
import { SetOfColumns, SetOfColumnsType } from '../../dialog/column-settings-dialog/set-of-columns';

export type SelectedEvent = { value: ColumnVisabilityPositionSetting[]; idSelectedSet: ResourceUUIDVersionIdentifier | null; isValid: boolean };

@Component({
  selector: 'app-column-hider-list',
  templateUrl: './column-hider-list.component.html',
  styleUrls: ['./column-hider-list.component.scss']
})
export class ColumnHiderListComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output()
  changes = new EventEmitter<SelectedEvent>();
  @Output()
  draggingChange = new EventEmitter<boolean>();
  @Output()
  frozenColumnsCountChange = new EventEmitter<number | null>();
  @Output()
  closeRequested = new EventEmitter<void>();
  @Input()
  datasource: string;
  @Input()
  expanded = false;
  @Input()
  inlineMode = false;
  // Liczba przypiętych kolumn danych: 0 = przypięta tylko kolumna "options", null = przypinanie wyłączone.
  @Input()
  frozenColumnsCount: number | null = null;
  // Kolumny przypięte na stałe (ColumnDefinition.locked), stojące w tabeli zaraz za "options". Na tej
  // liście ich nie ma, ale liczą się do frozenColumnsCount, bo ta liczy komórki tabeli od lewej.
  @Input()
  lockedColumnsCount = 0;

  // musi odpowiadać PrimeTableComponent.MAX_FROZEN_COLUMNS (bez importu — uniknięcie cyklu zależności)
  private readonly MAX_FROZEN_COLUMNS = 8;
  private readonly LIST_ITEM_HEIGHT = 28;
  // Wpis-znacznik w fields zapisywanego zestawu kolumn, przenoszący liczbę przypiętych kolumn
  // w polu position (-1 = przypinanie wyłączone). Serwer przechowuje fields jako jsonb i nie
  // waliduje nazw, a starsze wersje klienta ignorują wpisy niepasujące do żadnej kolumny —
  // dzięki temu przypięcie zapisuje się w zestawach bez zmian po stronie serwera.
  private readonly FROZEN_SET_MARKER = '__frozenColumnsCount';
  private previousFrozenColumnsCount: number | null = null;
  freezeMenuItems: MenuItem[] = [];
  freezeDividerDragging = false;
  private freezeDividerDragTop: number | null = null;
  private prevPinnedVisibleNames: string[] | null = null;
  private freezeShrinkSub: Subscription;
  @ViewChild('freezeMenu') freezeMenu: ContextMenuComponent;

  private initalizated$ = new Subject();
  formSub: Subscription;
  readonly FORM_KEYS = {
    IS_VISIBLE: 'isVisible',
    COLUMN_NAME: 'columnName',
    POSITION: 'position',
    WIDTH: 'width'
  };

  visabilityForm: FormArray = new FormArray([], [anyFieldsTrueValidatorFormArray('isVisible')]);
  private previousVisabilitySelection: ColumnVisabilityPositionSetting[] = [];
  private titlesMap: Record<string, string> = {};
  searchTerm = '';

  availableSetsOfColums: ColumnSet[] = [];

  get hasColumnSets(): boolean {
    return this.availableSetsOfColums.some(g => g.options.length > 0);
  }
  setMode = new BehaviorSubject<{ type: 'ADD' | 'EDIT'; id?: ResourceUUIDVersionIdentifier; initialFields?: any[] } | null>(null);
  setModeNull$ = this.setMode.asObservable().pipe(map(v => v == null ? true : false));
  setModeNotNull$ = this.setMode.asObservable().pipe(map(v => v != null ? true : false));
  setForm: FormGroup = new FormGroup({
    name: new FormControl(null, [Validators.required]),
    type: new FormControl(null, [Validators.required]),
    datasource: new FormControl(null, [Validators.required]),
    fields: this.visabilityForm
  });

  constructor(
    private colsService: SetOfColumnsService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) { }


  @Input()
  set settings(cols: ColumnVisabilityPositionSetting[]) {
    this.prevPinnedVisibleNames = null;
    cols.forEach(col => this.titlesMap[col.columnName] = col.title);
    this.visabilityForm.clear();
    cols.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .forEach((column: ColumnVisabilityPositionSetting, idx) => {
        const group = new FormGroup({
          [this.FORM_KEYS.COLUMN_NAME]: new FormControl(column.columnName),
          [this.FORM_KEYS.IS_VISIBLE]: new FormControl(column.isVisible),
          [this.FORM_KEYS.WIDTH]: new FormControl(column.width),
          [this.FORM_KEYS.POSITION]: new FormControl(idx)
        });
        this.visabilityForm.push(group);
      });
  }

  @Input()
  set columnsSetPreviouslySelected(id: ResourceUUIDVersionIdentifier) {
    this.initalizated$
      .pipe(take(1))
      .subscribe(() => {
        const set = flatten(this.availableSetsOfColums.map(v => v.options)).find(o => o.getVersionId().equals(id));
        if (set != null)
          this.editSetOfColumns(set);
      });
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.freezeShrinkSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.formSub = combineLatest([this.visabilityForm.valueChanges, this.setMode])
      .subscribe(([form, mode]) => this.changes.next({
        value: form,
        idSelectedSet: (mode != null && mode.type === 'EDIT' && isEqual(mode.initialFields, form)) ? mode.id : null,
        isValid: this.visabilityForm.valid
      }));
    this.freezeShrinkSub = this.visabilityForm.valueChanges
      .subscribe(() => this.shrinkFreezeOnHidingPinned());
  }

  // Ukrycie przypiętej kolumny nie ma przenosić przypięcia na kolejną widoczną —
  // zamiast tego zmniejsza liczbę przypiętych (granica zostaje przed ukrytą kolumną).
  private shrinkFreezeOnHidingPinned(): void {
    if (this.frozenColumnsCount == null) {
      this.prevPinnedVisibleNames = null;
      return;
    }
    const previouslyPinned = this.prevPinnedVisibleNames;
    if (previouslyPinned?.length) {
      const hiddenNames = new Set<string>();
      for (let i = 0; i < this.visabilityForm.length; i++) {
        const group = this.visabilityForm.at(i);
        if (!group.get(this.FORM_KEYS.IS_VISIBLE)?.value)
          hiddenNames.add(group.get(this.FORM_KEYS.COLUMN_NAME)?.value);
      }
      const hiddenPinnedCount = previouslyPinned.filter(name => hiddenNames.has(name)).length;
      if (hiddenPinnedCount > 0) {
        // Nie niżej niż kolumny stałe: tych ukryć się nie da, więc granica nie ma jak zejść przed nie.
        const newCount = Math.max(this.lockedColumnsCount, this.frozenColumnsCount - hiddenPinnedCount);
        this.frozenColumnsCount = newCount;
        this.frozenColumnsCountChange.emit(newCount);
      }
    }
    this.prevPinnedVisibleNames = this.currentPinnedVisibleNames();
  }

  // Liczba przypiętych kolumn widocznych na liście (bez stałych). Wszystko, co liczy się po pozycjach
  // listy - granica, podświetlenie, nazwy przypiętych - idzie po tej wartości, nie po frozenColumnsCount.
  private get listFrozenCount(): number {
    return Math.max(0, (this.frozenColumnsCount ?? 0) - this.lockedColumnsCount);
  }

  private currentPinnedVisibleNames(): string[] {
    const names: string[] = [];
    if (!this.listFrozenCount)
      return names;
    for (let i = 0; i < this.visabilityForm.length && names.length < this.listFrozenCount; i++) {
      const group = this.visabilityForm.at(i);
      if (group.get(this.FORM_KEYS.IS_VISIBLE)?.value)
        names.push(group.get(this.FORM_KEYS.COLUMN_NAME)?.value);
    }
    return names;
  }

  ngOnInit(): void {
    this.colsService.getByDataType(this.datasource)
      .pipe(mergeMap(c => c))
      .subscribe(s => {
        this.addOrCreateAvailableOption(s);
        this.initalizated$.next();
      });
  }

  private addOrCreateAvailableOption(set: SetOfColumns): void {
    const availableOptions = this.availableSetsOfColums.find(c => c.type === set.type)?.options;
    if (availableOptions == null)
      this.availableSetsOfColums.push(new ColumnSet(set.type, this.translateService.instant(`columns-${set.type}`), [set]));
    else
      availableOptions.push(set);
  }

  // Przy aktywnym wyszukiwaniu zaznacz/odznacz wszystko obejmuje tylko pozycje pasujące do frazy.
  isAllChecked(): boolean {
    const relevant = this.visabilityForm.controls.filter((_, i) => this.matchesSearch(i));
    return relevant.length > 0 && relevant.every(c => c.get(this.FORM_KEYS.IS_VISIBLE)?.value !== false);
  }

  toggleAllChecked(): void {
    const allChecked = this.isAllChecked();
    this.visabilityForm.controls.forEach((ctrl, i) => {
      if (this.matchesSearch(i))
        ctrl.get(this.FORM_KEYS.IS_VISIBLE).setValue(!allChecked);
    });
  }

  isSetFormDisabled(): boolean {
    return this.setForm.invalid || !this.setForm.dirty;
  }

  drop(event: { previousIndex: number; currentIndex: number }): void {
    const formArray = this.visabilityForm;
    const controls = formArray.controls;
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    controls.forEach((control, idx) => control.get(this.FORM_KEYS.POSITION).setValue(idx));
  }

  handleSetOfColumsChange(set: SetOfColumns): void {
    this.previousVisabilitySelection = this.cloneCurrentVisibility();
    this.previousFrozenColumnsCount = this.frozenColumnsCount;
    this.editSetOfColumns(set);
    this.sortVisabilitesByPosition();
  }

  private createFrozenMarkerField(): { columnName: string; isVisible: boolean; position: number; width: any } {
    return {
      columnName: this.FROZEN_SET_MARKER,
      isVisible: false,
      position: this.frozenColumnsCount ?? -1,
      width: null
    };
  }

  // Odczyt przypięcia z zestawu: brak znacznika (starsze zestawy) nie zmienia bieżącego przypięcia.
  private applyFrozenCountFromSet(set: SetOfColumns): void {
    const marker = set.fields?.find(f => f.columnName === this.FROZEN_SET_MARKER);
    if (marker != null)
      this.applyFrozenColumnsCount(marker.position >= 0 ? Math.min(marker.position, this.MAX_FROZEN_COLUMNS) : null);
  }

  editSetOfColumns(set: SetOfColumns): void {
    this.applyFrozenCountFromSet(set);
    const selectedFields = this.visabilityForm.value.map(v => {
      const matched = set.fields.find(f => f.columnName === v[this.FORM_KEYS.COLUMN_NAME]);
      v[this.FORM_KEYS.IS_VISIBLE] = matched != null;
      v[this.FORM_KEYS.POSITION] = matched != null ? matched.position : v[this.FORM_KEYS.POSITION];
      return v;
    }).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    selectedFields.forEach((v, idx) => v.position = idx);
    this.setForm.patchValue({ name: set.name, type: set.type, datasource: this.datasource, fields: selectedFields });
    this.setMode.next({ type: 'EDIT', id: set.getVersionId(), initialFields: selectedFields });
  }

  handleAddForUser = (): void => this.handleAddSet(SetOfColumnsType.USER);
  handleAddGeneral = (): void => this.handleAddSet(SetOfColumnsType.GENERAL);

  private handleAddSet(type: SetOfColumnsType): void {
    this.previousVisabilitySelection = this.cloneCurrentVisibility();
    this.previousFrozenColumnsCount = this.frozenColumnsCount;
    this.setForm.patchValue({ name: null, type, datasource: this.datasource, fields: this.visabilityForm.value });
    this.setMode.next({ type: 'ADD' });
  }

  handleResetSetSelection(): void {
    this.resetSetSelection();
    this.sortVisabilitesByPosition();
  };

  handleDeleteSet(): void {
    const resId = this.setMode.getValue()?.id;
    this.colsService.delete(resId).subscribe(() => {
      const set = this.availableSetsOfColums.find(c => c.type === this.setForm.value.type);
      set.options = set.options.filter(o => o.id !== resId.id);
      this.resetSetSelection();
    },
      _err => this.toastService.showErrorToast('error-while-delete-cols-set')
    );
  }

  // only visible columns should be sent to server
  handleSaveSetSelection(): void {
    const formValue = this.setForm.value;
    const visibleFields = formValue.fields.filter(f => f.isVisible);
    formValue.fields = [...visibleFields, this.createFrozenMarkerField()];
    const modType = this.setMode.getValue();
    if (modType.type === 'EDIT') {
      this.colsService.update(modType.id, formValue).subscribe(() => {
        const opt = this.availableSetsOfColums.find(c => c.type === formValue.type)
          ?.options?.find(o => o.id === modType.id.id);
        const newResId = modType.id.increaseVersion();
        Object.assign(opt, { ...formValue, version: newResId.version });
        this.setMode.next({ ...modType, id: newResId, initialFields: visibleFields });
        this.setForm.markAsPristine();
      },
        err => this.toastService.showErrorToast('error-while-update-cols-set', 'error', { error: this.translateService.instant(err.error) })
      );
    } else {
      this.colsService.save(formValue).subscribe((res: HttpResponse<any>) => {
        const location = res.headers.get('Location');
        const id = ResourceUUIDVersionIdentifier.createFromString(last(location.split('/')));
        const set = Object.assign(new SetOfColumns(), { ...formValue, ...id });
        this.addOrCreateAvailableOption(set);
        this.editSetOfColumns(set);
        this.setForm.markAsPristine();
      },
        err => this.toastService.showErrorToast('error-while-save-cols-set', 'error', { error: this.translateService.instant(err.error) })
      );
    }
  }

  getColumnLabel(idx: number): string {
    const value = this.visabilityForm.at(idx).get(this.FORM_KEYS.COLUMN_NAME)?.value;
    return this.titlesMap[value] ?? value;
  }

  matchesSearch(idx: number): boolean {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return true;
    return this.translateService.instant(this.getColumnLabel(idx)).toLowerCase().includes(term);
  }

  // Podczas filtrowania ukryte pozycje zastępujemy pustym miejscem na dole listy, aby
  // całkowita wysokość zawartości (a więc i okna dialogowego) nie zmieniała się i pole wyszukiwania nie skakało.
  // W trybie inline (menu z paska sterowania) kurczenie się listy jest pożądane, więc tam pomijamy wypełniacz.
  get searchSpacerHeight(): number {
    if (this.inlineMode || !this.searchTerm.trim()) return 0;
    const hidden = this.visabilityForm.controls.reduce((acc, _, i) => acc + (this.matchesSearch(i) ? 0 : 1), 0);
    return hidden * this.LIST_ITEM_HEIGHT;
  }

  onSearchKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    if (this.searchTerm) {
      this.searchTerm = '';
    } else {
      this.closeRequested.emit();
    }
  }

  getVisibleNumber(idx: number): number | null {
    if (!this.visabilityForm.at(idx).get(this.FORM_KEYS.IS_VISIBLE)?.value) return null;
    let count = 0;
    for (let i = 0; i <= idx; i++) {
      if (this.visabilityForm.at(i).get(this.FORM_KEYS.IS_VISIBLE)?.value) count++;
    }
    return count;
  }

  isFrozenItem(idx: number): boolean {
    if (this.frozenColumnsCount == null)
      return false;
    const isVisible = this.visabilityForm.at(idx)?.get(this.FORM_KEYS.IS_VISIBLE)?.value;
    // podgląd na żywo podczas przeciągania linii granicy
    if (this.freezeDividerDragTop != null)
      return isVisible && idx < this.freezeDividerDragTop / this.LIST_ITEM_HEIGHT;
    const visibleNumber = this.getVisibleNumber(idx);
    return visibleNumber != null && visibleNumber <= this.listFrozenCount;
  }

  openFreezeMenu(event: MouseEvent, idx: number): void {
    event.preventDefault();
    event.stopPropagation();
    // liczba widocznych kolumn do klikniętej włącznie (plus kolumny stałe, których lista nie pokazuje,
    // a które w tabeli stoją przed nimi) = docelowa liczba przypiętych ("freeze panes")
    const targetCount = this.visibleCountUpTo(idx) + this.lockedColumnsCount;
    this.freezeMenuItems = [
      {
        label: this.translateService.instant('freezeColumnsHere'),
        icon: 'mi mi-keep',
        disabled: targetCount > this.MAX_FROZEN_COLUMNS,
        command: () => this.applyFrozenColumnsCount(targetCount)
      },
      {
        label: this.translateService.instant('unfreezeColumns'),
        icon: 'mi mi-keep_off',
        disabled: this.frozenColumnsCount == null,
        command: () => this.applyFrozenColumnsCount(null)
      }
    ];
    this.freezeMenu.open(event, this.freezeMenuItems);
  }

  // linia granicy przykrywa pozycje listy, więc PPM na niej trafia w nią, a nie w wiersz —
  // menu odnosimy do ostatniej pozycji nad linią
  openFreezeMenuFromDivider(event: MouseEvent): void {
    this.openFreezeMenu(event, this.freezeBoundaryListIndex() - 1);
  }

  private applyFrozenColumnsCount(count: number | null): void {
    this.frozenColumnsCount = count;
    this.prevPinnedVisibleNames = this.currentPinnedVisibleNames();
    this.frozenColumnsCountChange.emit(count);
  }

  private visibleCountUpTo(idx: number): number {
    let count = 0;
    for (let i = 0; i <= idx && i < this.visabilityForm.length; i++) {
      if (this.visabilityForm.at(i).get(this.FORM_KEYS.IS_VISIBLE)?.value)
        count++;
    }
    return count;
  }

  // === Przesuwalna linia granicy przypięcia ===
  // Linia leży pod ostatnią przypiętą pozycją; dosunięta do samej góry oznacza count = 0
  // (przypięta tylko kolumna "options").
  get freezeDividerTop(): number {
    if (this.freezeDividerDragTop != null)
      return this.freezeDividerDragTop;
    return this.freezeBoundaryListIndex() * this.LIST_ITEM_HEIGHT;
  }

  get freezeDividerVisible(): boolean {
    // przy aktywnym wyszukiwaniu lista jest przefiltrowana wizualnie, więc pozycja linii
    // liczona z indeksów byłaby błędna — ukrywamy ją (przeciąganie też jest wtedy wyłączone)
    return this.frozenColumnsCount != null && !this.searchTerm.trim();
  }

  // liczba pozycji listy (łącznie z ukrytymi) leżących nad granicą przypięcia
  private freezeBoundaryListIndex(): number {
    const count = this.listFrozenCount;
    if (count <= 0)
      return 0;
    let visible = 0;
    for (let i = 0; i < this.visabilityForm.length; i++) {
      if (this.visabilityForm.at(i).get(this.FORM_KEYS.IS_VISIBLE)?.value) {
        visible++;
        if (visible === count)
          return i + 1;
      }
    }
    return this.visabilityForm.length;
  }

  startFreezeDividerDrag(event: MouseEvent): void {
    if (event.button !== 0)
      return;
    event.preventDefault();
    event.stopPropagation();
    const list = (event.target as HTMLElement).closest('.mat-list');
    if (!list)
      return;
    this.freezeDividerDragging = true;
    const maxIndex = this.visabilityForm.length;
    const onMove = (moveEvent: MouseEvent): void => {
      const rect = list.getBoundingClientRect();
      const offset = moveEvent.clientY - rect.top + list.scrollTop;
      const index = Math.max(0, Math.min(Math.round(offset / this.LIST_ITEM_HEIGHT), maxIndex));
      this.freezeDividerDragTop = index * this.LIST_ITEM_HEIGHT;
      // gdy lista jest w overlayu tabeli (OnPush), tick z mousemove nie sprawdzi tego widoku
      // bez oznaczenia przodków jako dirty — bez tego linia skacze dopiero po puszczeniu
      this.cdr.markForCheck();
    };
    const onUp = (): void => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const boundaryIndex = Math.round((this.freezeDividerDragTop ?? 0) / this.LIST_ITEM_HEIGHT);
      this.freezeDividerDragTop = null;
      this.freezeDividerDragging = false;
      this.applyFrozenColumnsCount(
        Math.min(this.visibleCountUpTo(boundaryIndex - 1) + this.lockedColumnsCount, this.MAX_FROZEN_COLUMNS));
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  private resetSetSelection(): void {
    this.setMode.next(null);
    this.setForm.patchValue({
      name: null,
      type: null,
      datasource: null,
      fields: cloneDeep(this.previousVisabilitySelection)
    });
    this.previousVisabilitySelection = [];
    if (this.previousFrozenColumnsCount !== this.frozenColumnsCount)
      this.applyFrozenColumnsCount(this.previousFrozenColumnsCount);
  }

  private cloneCurrentVisibility(): ColumnVisabilityPositionSetting[] {
    return cloneDeep(this.visabilityForm.value);
  }

  sortVisabilitesByPosition(): void {
    this.visabilityForm?.controls.sort((a, b) =>
      (a.get(this.FORM_KEYS.POSITION)?.value ?? 0) - (b.get(this.FORM_KEYS.POSITION)?.value ?? 0)
    );
  }

}


class ColumnSet {
  constructor(public type: string, public readonly name: string, public options: SetOfColumns[]) { }
}
